import * as THREE from 'three';

const WALL_URLS = [
  '/maps/walls.svg',
  '/maps/walls2.svg',
  '/maps/walls3.svg',
  '/maps/walls4.svg',
  '/maps/walls5.svg',
];
const FLOOR_URL = '/maps/floor.svg';
const ATLAS_TILE = 128;
const FLOOR_VARIANT_COUNT = 16;

// ─── Hashing (mirror of LabyrinthState.debrisRng style, but per-variant) ──

function mix32(h: number): number {
  h = ((h >> 16) ^ h) * 0x45d9f3b >>> 0;
  h = ((h >> 16) ^ h) * 0x45d9f3b >>> 0;
  return ((h >> 16) ^ h) >>> 0;
}

function debrisHash(variant: number, i: number): number {
  return mix32(((variant * 73856093) ^ (i * 83492791)) >>> 0);
}

// ─── Wall atlas (5 variants horizontally) ──
//
// Two sibling textures share one canvas-sized layout:
//   wallAtlas   — full wall SVGs, used as the color map.
//   wallRedMask — only the red-dominant pixels (emergency lamps, LEDs,
//                 vent glows, hazard stripes); everything else transparent.
//                 The shader samples this at the same UVs as the atlas and
//                 adds `mask.rgb * perTilePulse` on top of the lit color —
//                 that's how each wall tile flashes independently despite
//                 all tiles of one variant sharing the atlas cell.

function buildWallAtlasAndMask(): { atlas: THREE.CanvasTexture; redMask: THREE.CanvasTexture } {
  const canvas = document.createElement('canvas');
  canvas.width  = ATLAS_TILE * WALL_URLS.length;
  canvas.height = ATLAS_TILE;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width  = canvas.width;
  maskCanvas.height = canvas.height;
  const maskCtx = maskCanvas.getContext('2d')!;

  const atlas = new THREE.CanvasTexture(canvas);
  atlas.colorSpace = THREE.SRGBColorSpace;
  atlas.anisotropy = 4;
  atlas.wrapS = THREE.ClampToEdgeWrapping;
  atlas.wrapT = THREE.ClampToEdgeWrapping;

  const redMask = new THREE.CanvasTexture(maskCanvas);
  redMask.colorSpace = THREE.SRGBColorSpace;
  redMask.anisotropy = 4;
  redMask.wrapS = THREE.ClampToEdgeWrapping;
  redMask.wrapT = THREE.ClampToEdgeWrapping;

  WALL_URLS.forEach((url, i) => {
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, i * ATLAS_TILE, 0, ATLAS_TILE, ATLAS_TILE);
      extractRedMask(ctx, maskCtx, i);
      atlas.needsUpdate = true;
      redMask.needsUpdate = true;
    };
    img.src = url;
  });
  return { atlas, redMask };
}

/** Scan a freshly drawn wall variant for red-dominant pixels and write their
 *  color into the mask canvas at the same atlas coords; non-red pixels stay
 *  transparent. The `r > g*1.7 && r > b*1.7` heuristic admits genuine red
 *  lights (LEDs, stripes, glows) while rejecting rust/scorch/dirt tones. */
function extractRedMask(
  src: CanvasRenderingContext2D,
  dst: CanvasRenderingContext2D,
  variantIndex: number,
): void {
  const x0 = variantIndex * ATLAS_TILE;
  const srcImg = src.getImageData(x0, 0, ATLAS_TILE, ATLAS_TILE);
  const out = new ImageData(ATLAS_TILE, ATLAS_TILE);
  const sp = srcImg.data;
  const dp = out.data;
  for (let p = 0, n = ATLAS_TILE * ATLAS_TILE; p < n; p++) {
    const o = p * 4;
    const r = sp[o]!, g = sp[o + 1]!, b = sp[o + 2]!, a = sp[o + 3]!;
    if (a !== 0 && r >= 15 && r > g * 1.7 && r > b * 1.7) {
      dp[o]     = r;
      dp[o + 1] = g;
      dp[o + 2] = b;
      dp[o + 3] = 255;
    }
    // else: leave fully transparent black — already zero-initialised.
  }
  dst.putImageData(out, x0, 0);
}

// ─── Floor atlas: base floor + procedural debris per variant ──

/**
 * Mirrors the 7 debris types in LabyrinthState.drawFloorDebris but in Canvas2D,
 * scaled from the 32-px source tile up to ATLAS_TILE (4× bigger).
 */
function drawDebris(ctx: CanvasRenderingContext2D, ox: number, oy: number, variant: number): void {
  const rng = (i: number) => debrisHash(variant, i);
  const seed = rng(0);

  // ~50% of variants stay empty → matches the 2D "sparse debris" feel.
  if ((seed % 100) > 50) return;

  const SCALE = ATLAS_TILE / 32; // original values assume 32-px tiles
  const kind = seed % 7;

  ctx.save();

  if (kind === 0) {
    // Oil stain
    const cx = ox + ((rng(1) % 20) + 6) * SCALE;
    const cy = oy + ((rng(2) % 20) + 6) * SCALE;
    const r  = (2.5 + (rng(3) % 30) / 10) * SCALE;
    ctx.fillStyle = 'rgba(10,12,16,0.35)';
    ellipse(ctx, cx, cy, r * 1.3, r);
    ctx.fill();
    ctx.fillStyle = 'rgba(20,24,30,0.20)';
    ellipse(ctx, cx + SCALE, cy + SCALE * 0.5, r * 0.7, r * 0.5);
    ctx.fill();
  } else if (kind === 1) {
    // Rust stain
    const cx = ox + ((rng(1) % 22) + 5) * SCALE;
    const cy = oy + ((rng(2) % 22) + 5) * SCALE;
    const r  = (2 + (rng(3) % 25) / 10) * SCALE;
    ctx.fillStyle = 'rgba(58,26,10,0.30)';
    ellipse(ctx, cx, cy, r, r * 0.8);
    ctx.fill();
  } else if (kind === 2) {
    // Scratch marks
    const count = 1 + rng(4) % 3;
    ctx.strokeStyle = 'rgba(58,62,72,0.5)';
    ctx.lineWidth = 0.6 * SCALE;
    for (let i = 0; i < count; i++) {
      const sx = ox + ((rng(10 + i * 2) % 26) + 3) * SCALE;
      const sy = oy + ((rng(11 + i * 2) % 26) + 3) * SCALE;
      const ang = (rng(20 + i) % 628) / 100;
      const len = (4 + (rng(30 + i) % 8)) * SCALE;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx + Math.cos(ang) * len, sy + Math.sin(ang) * len);
      ctx.stroke();
    }
  } else if (kind === 3) {
    // Small metal debris rectangles
    const count = 2 + rng(4) % 3;
    ctx.fillStyle = 'rgba(74,80,96,0.4)';
    for (let i = 0; i < count; i++) {
      const dx = ox + ((rng(10 + i) % 26) + 3) * SCALE;
      const dy = oy + ((rng(20 + i) % 26) + 3) * SCALE;
      const w  = (1 + (rng(30 + i) % 3)) * SCALE;
      const h  = (1 + (rng(40 + i) % 2)) * SCALE;
      ctx.fillRect(dx, dy, w, h);
    }
  } else if (kind === 4) {
    // Scorch mark
    const cx = ox + ((rng(1) % 20) + 6) * SCALE;
    const cy = oy + ((rng(2) % 20) + 6) * SCALE;
    ctx.fillStyle = 'rgba(6,6,8,0.20)';
    circle(ctx, cx, cy, 4 * SCALE); ctx.fill();
    ctx.fillStyle = 'rgba(10,10,14,0.30)';
    circle(ctx, cx, cy, 2 * SCALE); ctx.fill();
  } else if (kind === 5) {
    // Drip trail
    const sx = ox + ((rng(1) % 24) + 4) * SCALE;
    const sy = oy + ((rng(2) % 24) + 4) * SCALE;
    const ang = (rng(3) % 628) / 100;
    const drops = 3 + rng(4) % 3;
    for (let i = 0; i < drops; i++) {
      const dx = sx + Math.cos(ang) * i * 3 * SCALE;
      const dy = sy + Math.sin(ang) * i * 3 * SCALE;
      const r  = (0.6 + (rng(10 + i) % 10) / 15) * SCALE;
      ctx.fillStyle = `rgba(26,42,32,${0.35 - i * 0.05})`;
      circle(ctx, dx, dy, r); ctx.fill();
    }
  } else {
    // Dust patch
    const cx = ox + ((rng(1) % 18) + 7) * SCALE;
    const cy = oy + ((rng(2) % 18) + 7) * SCALE;
    const r  = (3 + (rng(3) % 30) / 10) * SCALE;
    ctx.fillStyle = 'rgba(30,32,40,0.15)';
    ellipse(ctx, cx, cy, r * 1.4, r);
    ctx.fill();
  }

  ctx.restore();
}

function ellipse(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number): void {
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
}
function circle(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
}

function buildFloorAtlas(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width  = ATLAS_TILE * FLOOR_VARIANT_COUNT;
  canvas.height = ATLAS_TILE;
  const ctx = canvas.getContext('2d')!;

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;

  const img = new Image();
  img.onload = () => {
    for (let v = 0; v < FLOOR_VARIANT_COUNT; v++) {
      const ox = v * ATLAS_TILE;
      ctx.drawImage(img, ox, 0, ATLAS_TILE, ATLAS_TILE);
      // Layer 1–2 debris blobs per variant for richer variety.
      drawDebris(ctx, ox, 0, v);
      drawDebris(ctx, ox, 0, v + 137);
    }
    tex.needsUpdate = true;
  };
  img.src = FLOOR_URL;
  return tex;
}

// ─── Exports ───────────────────────────────────────────────

const _wallTextures = buildWallAtlasAndMask();

export const LabTextures = {
  floorAtlas: buildFloorAtlas(),
  floorVariantCount: FLOOR_VARIANT_COUNT,
  wallAtlas: _wallTextures.atlas,
  wallRedMask: _wallTextures.redMask,
  wallVariantCount: WALL_URLS.length,
};

export function wallVariantFor(x: number, y: number): number {
  const h = ((x * 73856093) ^ (y * 19349663)) >>> 0;
  return h % LabTextures.wallVariantCount;
}

/** Each wall face gets its own variant so two adjacent walls don't echo the
 *  same sprite across the seam — kills the most visible repeat pattern. */
export function wallVariantForFace(x: number, y: number, dir: number): number {
  const h = mix32(((x * 73856093) ^ (y * 19349663) ^ (dir * 2971215073)) >>> 0);
  return h % LabTextures.wallVariantCount;
}

export function floorVariantFor(x: number, y: number): number {
  // Different prime mix than walls so variants aren't correlated.
  const h = ((x * 2654435761) ^ (y * 40503)) >>> 0;
  return h % LabTextures.floorVariantCount;
}

/** Orientation 0..7: lower 2 bits = 90° rotation steps, bit 2 = horizontal mirror.
 *  Multiplies effective variant count 8× without needing more source art. */
export function floorOrientFor(x: number, y: number): number {
  return mix32(((x * 374761393) ^ (y * 668265263)) >>> 0) & 7;
}

/** Top face of wall tiles (visible from above). Full 8-way orientation is fine —
 *  the texture is seen looking straight down so rotation never looks wrong. */
export function wallTopOrientFor(x: number, y: number): number {
  return mix32(((x * 1274126177) ^ (y * 2246822519)) >>> 0) & 7;
}

/** Wall side faces: horizontal mirror only. Full rotation would put vertical
 *  structure (panel lines, bolts) on its side, which reads as obviously wrong. */
export function wallFaceFlipFor(x: number, y: number, dir: number): boolean {
  return (mix32(((x * 1103515245) ^ (y * 12345) ^ (dir * 1140671485)) >>> 0) & 1) === 1;
}
